/**
 * Per-navigation context threaded through the personalised invitee welcome
 * flow. Captured by `/invites/:uuid` after a successful accept and forwarded
 * via React Router's `location.state` through `/welcome/password` →
 * `/welcome/profile` → `/welcome/role-tour`.
 *
 * Why navigation state instead of a server-side gate: the real BE doesn't
 * (yet) carry a `welcomedAt` field on TeamUser, so there's no row to ask
 * "is this membership unwelcomed?" The flow is therefore *invite-link-driven*
 * — it fires when, and only when, the user comes through `/invites/:uuid`
 * and accepts. A full reload mid-flow loses this state and drops the user
 * back to `/`; that's an acceptable degradation for a one-shot welcome.
 *
 * Why not URL query params: the team/role values are personalisation, not
 * security gates, so the designer's "untrusted query params" rejection
 * doesn't apply — but `location.state` keeps URLs clean and is the
 * idiomatic SPA pattern.
 */
export interface WelcomeNavState {
  teamId: number
  teamName: string
  teamType: string
  role: string
}

/** Type-narrowing read of `location.state` — defensive against a refresh or
 *  direct URL access that lands on a welcome page without context. */
export function readWelcomeNavState(state: unknown): WelcomeNavState | null {
  if (!state || typeof state !== "object") return null
  const s = state as Record<string, unknown>
  if (
    typeof s.teamId === "number" &&
    typeof s.teamName === "string" &&
    typeof s.teamType === "string" &&
    typeof s.role === "string"
  ) {
    return {
      teamId: s.teamId,
      teamName: s.teamName,
      teamType: s.teamType,
      role: s.role,
    }
  }
  return null
}
